import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import React, { memo, useRef, useState } from "react";
import { useIsomorphicLayoutEffect } from "@swiggy-private/core-ui";
import { QUICK_CHECKOUT_TEST_IDS } from "@swiggy-private/cart-test-ids";
import { PILL_WIDTH_ANIMATION_MS } from "./animationTiming";
import {
    Container,
    Content,
    SavingsPill,
    PillSolidSurface,
    PillGradientSurface,
    PillContent,
    HeaderCta,
    HiddenMeasure,
    Divider,
    IconWrapper,
    DeliveryCountTextWrapper,
} from "./styles";

import { GenericIcon } from "@swiggy-private/dls-web-icons/dist/GenericIconWeb";
import { default as chevronRightIcon } from "@swiggy-private/dls-web-icons/dist/web/generic/chevronRight/12";

const MEASURE_BUFFER_PX = 4;

interface QuickCheckoutHeaderProps {
    savingsText?: string;
    /** Collapsed copy used only for width measurement (not the animated visible text). */
    collapsedSavingsText?: string;
    /** Multi-delivery copy, e.g. "2 Deliveries". Omitted when count ≤ 1. */
    deliveryCountText?: string;
    headerCtaText?: string;
    onHeaderCtaClick?: () => void;
    isSavingsStripExpanded?: boolean;
    isViewCartHidden?: boolean;
    isLoading?: boolean;
    hasSavings?: boolean;
}

const readContentWidthPx = (element: HTMLElement | null): number => {
    if (!element) {
        return 0;
    }
    return Math.ceil(element.scrollWidth) + MEASURE_BUFFER_PX;
};

const QuickCheckoutHeader = (props: QuickCheckoutHeaderProps) => {
    const {
        savingsText,
        collapsedSavingsText,
        deliveryCountText,
        headerCtaText,
        onHeaderCtaClick,
        isSavingsStripExpanded = false,
        isViewCartHidden = false,
        isLoading = false,
        hasSavings = false,
    } = props;
    const [collapsedMaxWidthPx, setCollapsedMaxWidthPx] = useState(0);
    const [expandedMaxWidthPx, setExpandedMaxWidthPx] = useState(0);
    // Latched because the CTA reveals before the pill finishes collapsing, so `isViewCartHidden`
    // can no longer mark the end of the width tween.
    const [isPillCollapsing, setIsPillCollapsing] = useState(false);
    const contentRef = useRef<HTMLDivElement>(null);
    const measureRef = useRef<HTMLDivElement>(null);
    const wasSavingsStripExpandedRef = useRef(isSavingsStripExpanded);

    // The pill only takes over the row once it starts growing. During the CTA fade that precedes
    // it the row is static, so nothing has to leave the flow yet.
    const isPillTakingOverRow = isSavingsStripExpanded || isPillCollapsing;
    const isLayoutAnimating = isPillTakingOverRow || isViewCartHidden;

    // Layout effect so the delivery-count text leaves the flow before paint, otherwise it renders
    // for a frame beside a still-expanded pill.
    useIsomorphicLayoutEffect(() => {
        if (isSavingsStripExpanded) {
            wasSavingsStripExpandedRef.current = true;
            return;
        }

        if (!wasSavingsStripExpandedRef.current) {
            return;
        }

        wasSavingsStripExpandedRef.current = false;
        setIsPillCollapsing(true);
        const collapseTimeoutId = setTimeout(() => setIsPillCollapsing(false), PILL_WIDTH_ANIMATION_MS);

        return () => clearTimeout(collapseTimeoutId);
    }, [isSavingsStripExpanded]);

    useIsomorphicLayoutEffect(() => {
        // Skip while widths are locked for the in-flight animation.
        if (isLayoutAnimating) {
            return;
        }

        let cancelled = false;

        const measure = () => {
            if (cancelled) {
                return;
            }

            if (contentRef.current) {
                const style = window.getComputedStyle(contentRef.current);
                const horizontalPadding = parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
                setExpandedMaxWidthPx(contentRef.current.clientWidth - horizontalPadding);
            }

            setCollapsedMaxWidthPx(readContentWidthPx(measureRef.current));
        };

        measure();
        document.fonts?.ready.then(measure).catch(() => undefined);

        return () => {
            cancelled = true;
        };
    }, [collapsedSavingsText, headerCtaText, isLayoutAnimating]);

    return (
        <Container>
            <Content ref={contentRef}>
                {hasSavings && (
                    <SavingsPill
                        isVisibility={!!savingsText}
                        isExpanded={isSavingsStripExpanded}
                        isLayoutAnimating={isLayoutAnimating}
                        collapsedMaxWidthPx={collapsedMaxWidthPx}
                        expandedMaxWidthPx={expandedMaxWidthPx}
                    >
                        <PillSolidSurface isExpanded={isSavingsStripExpanded} aria-hidden />
                        <PillGradientSurface isExpanded={isSavingsStripExpanded} aria-hidden />
                        <PillContent>
                            <Text maxLines={1} color="positive" variant="V2.body2.neutral.bold">
                                {savingsText}
                            </Text>
                        </PillContent>
                    </SavingsPill>
                )}
                {deliveryCountText && (
                    <DeliveryCountTextWrapper isFaded={isViewCartHidden} isRemoved={isPillTakingOverRow}>
                        <Text maxLines={1} color="text_Med_Emphasis" variant="V2.body2.neutral.medium">
                            {deliveryCountText}
                        </Text>
                    </DeliveryCountTextWrapper>
                )}

                <HeaderCta
                    data-testid={QUICK_CHECKOUT_TEST_IDS.HEADER_CTA}
                    isLoading={isLoading}
                    isHidden={isViewCartHidden}
                    isPinnedRight={isPillTakingOverRow}
                    onClick={onHeaderCtaClick}
                >
                    <Text maxLines={1} color="text_Low_Emphasis" variant="V2.body2.neutral.regular">
                        {headerCtaText}
                    </Text>
                    <IconWrapper>
                        <GenericIcon Icon={chevronRightIcon} fillColor="text_Low_Emphasis" />
                    </IconWrapper>
                </HeaderCta>
            </Content>
            {/* Variant must match the pill copy, else the locked collapsed width is wrong. */}
            <HiddenMeasure ref={measureRef} aria-hidden>
                <Text maxLines={1} color="positive" variant="V2.body2.neutral.bold">
                    {collapsedSavingsText}
                </Text>
            </HiddenMeasure>
            <Divider />
        </Container>
    );
};

export default memo(QuickCheckoutHeader);